import { Trans, useLingui } from '@lingui/react/macro';

import { BaseMoneyCell } from '@/components/ArticlesTableCells';
import AlertBar from '@/design_system/AlertBar';
import PhotoCard from '@/design_system/PhotoCard';
import { PhotoCardProps } from '@/design_system/PhotoCard/PhotoCard';
import Stack from '@/design_system/Stack';
import { Body, Column, Header, Row, Table } from '@/design_system/Table/Table';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconExchange from '@/icons/Exchange.svg';
import { getProductPictureFromArticleColor, useGetArticleName } from '@/models/article';
import { ArticleWithRelations } from '@/models/request';
import { useDatabaseTranslation } from '@/services/i18n';
import { useGetExchangeTypes } from '@/utils/exchangeTypes';
import { formatCurrency } from '@/utils/number';
import useViewPort from '@/utils/useViewport';

type ExchangeVariant = 'proposal' | 'confirmed';

type ArticleExchange = NonNullable<ArticleWithRelations['snapshot']['articleExchange']>;

export const ClientExchangeDetails = ({
  article,
  variant = 'proposal',
}: {
  article: ArticleWithRelations;
  variant?: ExchangeVariant;
}) => {
  const { t } = useLingui();

  const isProposal = variant === 'proposal';
  const articleExchange = article.snapshot.articleExchange;

  const columnWidths = [
    'minmax(200px, 1fr)',
    ...(isProposal ? ['minmax(200px, 1fr)'] : []),
    '140px',
  ];

  return (
    <Stack gap="0.75rem">
      <Table
        theme="brand"
        ariaLabel={t({ id: 'client.article.services', message: 'Services' })}
        columnWidths={columnWidths}
        bordered
      >
        <Header>
          <Row>
            <Column>
              {isProposal ? (
                <Trans id="client.article.services.column.service">Service required</Trans>
              ) : (
                <Trans id="client.article.services.column.service.done">Service done</Trans>
              )}
            </Column>

            {isProposal && (
              <Column>
                <Trans id="client.article.services.column.replacement">Replacement item</Trans>
              </Column>
            )}

            <Column justifyContent="flex-end">
              <Trans id="client.article.services.column.price">Price (incl. VAT)</Trans>
            </Column>
          </Row>
        </Header>

        <Body>
          <Row>
            <Column>
              {isProposal && <IconExchange style={{ marginRight: '.5em' }} />}
              <ExchangeServiceName article={article} variant={variant} />
            </Column>

            {isProposal && (
              <Column>
                {!!articleExchange && (
                  <ReplacementProductCell articleExchange={articleExchange} photoSize="medium" />
                )}
              </Column>
            )}

            <Column justifyContent="flex-end">
              <ExchangePriceCell article={article} />
            </Column>
          </Row>
        </Body>
      </Table>

      <RecyclingAlert variant={variant} />
    </Stack>
  );
};

export const ClientExchangeCard = ({
  article,
  variant = 'proposal',
}: {
  article: ArticleWithRelations;
  variant?: ExchangeVariant;
}) => {
  const articleExchange = article.snapshot.articleExchange;
  const { isMobile } = useViewPort();
  const isProposal = variant === 'proposal';

  return (
    <>
      <Stack
        gap="0.75rem"
        style={
          isMobile
            ? {
                padding: '12px',
                border: '1px solid var(--color-neutral-300)',
                borderRadius: '8px',
              }
            : {}
        }
      >
        <Stack
          row
          gap="1rem"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="nowrap"
        >
          <Stack row flexWrap="nowrap" gap="0.25rem" alignItems="center">
            {isProposal && <IconExchange style={{ marginRight: '.5em' }} />}
            <ExchangeServiceName article={article} variant={variant} />
          </Stack>

          <ExchangePriceCell article={article} />
        </Stack>

        {isProposal && !!articleExchange && (
          <ReplacementProductCell articleExchange={articleExchange} photoSize="small" />
        )}
      </Stack>
      <RecyclingAlert variant={variant} />
    </>
  );
};

const ExchangePriceCell = ({ article }: { article: ArticleWithRelations }) => {
  const { t } = useLingui();

  const clientPrice = article.snapshot.organizationPrice?.amountPerCurrency?.[0];
  const clientRefusalBadgeText =
    article.cancellationDetail?.type === 'requestor_refusal'
      ? t({ id: 'client.articles.service-refused', message: 'Service refused' })
      : undefined;

  return (
    <BaseMoneyCell
      price={clientPrice}
      clientRefusalBadgeText={clientRefusalBadgeText}
      showAmountBeforeTaxes={false}
    />
  );
};

const ExchangeServiceName = ({
  article,
  variant,
}: {
  article: ArticleWithRelations;
  variant: ExchangeVariant;
}) => {
  const { exchangeTypeOptions } = useGetExchangeTypes();
  const getArticleName = useGetArticleName();

  const articleExchange = article.snapshot.articleExchange;
  const exchangeType = articleExchange?.type;

  const subExchangeTypeLabel = exchangeType
    ? exchangeTypeOptions.find((option) => option.value === exchangeType)?.label
    : undefined;

  if (variant === 'confirmed') {
    const exchangedArticleName = getArticleName({ article });
    const serialNumber = article.uniqueArticle?.serialNumber;

    return (
      <Stack gap="0.125rem" alignItems="start">
        <Paragraph>
          {subExchangeTypeLabel ?? <Trans id="service-choice.radio.exchange.label">Exchange</Trans>}
        </Paragraph>
        {!!exchangedArticleName && (
          <InlineText size="200" color="secondary" align="left">
            <Trans id="client.article.services.exchange.confirmed.replacement-item">
              Replacement item for {exchangedArticleName}
            </Trans>
            {!!serialNumber && (
              <>
                {' • '}
                <Trans id="client.article.services.exchange.confirmed.serial">
                  Serial number:
                </Trans>{' '}
                {serialNumber}
              </>
            )}
          </InlineText>
        )}
      </Stack>
    );
  }

  if (subExchangeTypeLabel) {
    return <Paragraph>{subExchangeTypeLabel}</Paragraph>;
  }

  const isSameProductReplacement =
    !!article.productId && article.productId === articleExchange?.replacementProductId;

  return isSameProductReplacement ? (
    <InlineText color="secondary">
      <Trans id="client.article.services.column.exchange.same.product">
        Exchange with the same product
      </Trans>
    </InlineText>
  ) : (
    <InlineText color="secondary">
      <Trans id="client.article.services.column.exchange.different.product">
        Exchange with a different product
      </Trans>
    </InlineText>
  );
};

const RecyclingAlert = ({ variant }: { variant: ExchangeVariant }) => (
  <AlertBar type="info">
    {variant === 'confirmed' ? (
      <InlineText color="secondary">
        <Trans id="client.article.services.exchange.recycling.confirmed">
          Your original item has been sent for recycling.
        </Trans>
      </InlineText>
    ) : (
      <InlineText color="secondary">
        <Trans id="client.article.services.exchange.recycling.pending">
          Once the replacement item is received, your original item will be sent for recycling.
        </Trans>
      </InlineText>
    )}
  </AlertBar>
);

const ReplacementProductCell = ({
  articleExchange,
  photoSize,
}: {
  articleExchange: ArticleExchange;
  photoSize?: PhotoCardProps['size'];
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();

  const replacementProduct = articleExchange.replacementProduct;

  if (!replacementProduct) {
    return null;
  }

  const name = replacementProduct.name;

  const photoLabel = t({
    id: 'article.photo.label',
    message: `Photo of ${name}`,
  });

  const color = articleExchange.replacementColor ? (
    <span>{_db(articleExchange.replacementColor)}</span>
  ) : null;
  const size = articleExchange.replacementSize ? (
    <span>{_db(articleExchange.replacementSize)}</span>
  ) : null;
  const retailPrice = replacementProduct.data.purchaseValue ? (
    <>
      <Trans id="article.retail-price">Retail price:</Trans>{' '}
      <span>
        {formatCurrency(
          replacementProduct.data.purchaseValue.amount,
          replacementProduct.data.purchaseValue.currency
        )}
      </span>
    </>
  ) : null;

  const attributes = [color, size, retailPrice].filter((item) => !!item);

  const replacementProductPicture = getProductPictureFromArticleColor({
    articleExchange,
  });

  return (
    <Stack row gap="0.75rem" alignItems="center" flexWrap="nowrap" style={{ minWidth: 0 }}>
      <PhotoCard
        url={replacementProductPicture ?? undefined}
        name={photoLabel}
        alt={photoLabel}
        size={photoSize ?? 'medium'}
        hideMoreIcon
      />
      <Stack alignItems="start">
        <Paragraph weight="medium" ellipsis title={replacementProduct.name}>
          {replacementProduct.name}
        </Paragraph>
        <InlineText size="200" color="secondary">
          <Trans id="article.reference.short">Ref:</Trans> {replacementProduct.externalId}
        </InlineText>

        {!!attributes.length && (
          <Stack row gap="0.25rem" flexWrap="wrap" alignItems="center">
            {attributes.map((item, index) => (
              <Stack key={index} row gap="0.25rem" alignItems="center" flexWrap="nowrap">
                {index > 0 && (
                  <Paragraph size="200" color="secondary">
                    <span>•</span>
                  </Paragraph>
                )}
                <Paragraph size="200" color="secondary">
                  {item}
                </Paragraph>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
